import { forwardRef, InputHTMLAttributes, ReactNode } from 'react';
import Input from './Input';

export interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label?: ReactNode;
  error?: string;
  hint?: string;
  required?: boolean;
  id?: string;
  className?: string;
  inputClassName?: string;
  type?: string;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  (
    {
      label,
      error,
      hint,
      required = false,
      id,
      className = '',
      inputClassName = '',
      type = 'text',
      ...props
    },
    ref
  ) => {
    const fieldId = id || props.name;

    return (
      <div className={`space-y-1.5 ${className}`}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-sm font-medium text-light-text dark:text-dark-text"
          >
            {label}
            {required && <span className="text-error ml-0.5">*</span>}
          </label>
        )}
        <Input
          ref={ref}
          id={fieldId}
          type={type}
          error={!!error}
          className={inputClassName}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined}
          {...props}
        />
        {error && (
          <p
            id={`${fieldId}-error`}
            className="text-xs text-error animate-fade-in"
            role="alert"
          >
            {error}
          </p>
        )}
        {hint && !error && (
          <p
            id={`${fieldId}-hint`}
            className="text-xs text-light-muted dark:text-dark-muted"
          >
            {hint}
          </p>
        )}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
